import { z } from 'zod'

/**
 * Who asked for a job, travelling with the job.
 *
 * Attribution inside one process is ambient, but a queue is a process boundary
 * and an AsyncLocalStorage does not cross it. Without this, everything the
 * worker spends is "a background job" — including the pull a user set off by
 * clicking inspect, which is exactly the distinction the ledger exists to draw.
 *
 * It rides on the payload rather than in a table keyed by job id: pg-boss
 * already carries the payload everywhere the job goes, and a side table would
 * need its own retention and its own join.
 */
export const jobOriginSchema = z.object({
  actor: z.enum(['job', 'user', 'system']),
  userId: z.string().optional(),
  organizationId: z.string().optional(),
})

export type JobOrigin = z.infer<typeof jobOriginSchema>

/** The key the origin travels under. Not part of any payload schema on purpose:
 * handlers never read it, the runner strips it before they see the payload, and
 * adding it to every schema would be churn for something no handler wants. */
export const JOB_ORIGIN_KEY = '__origin'

/**
 * Reads an origin off a raw job payload, tolerating anything. A malformed
 * origin means unattributed spend, never a failed job — the work is worth more
 * than the label on it.
 */
export function readJobOrigin(data: unknown): JobOrigin | null {
  if (!data || typeof data !== 'object') return null
  const raw = (data as Record<string, unknown>)[JOB_ORIGIN_KEY]
  const parsed = jobOriginSchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

/** Attaches an origin to a payload about to be enqueued. */
export function withJobOrigin<T extends object>(
  payload: T,
  origin: JobOrigin | null,
): T & { [JOB_ORIGIN_KEY]?: JobOrigin } {
  return origin ? { ...payload, [JOB_ORIGIN_KEY]: origin } : payload
}
